import type {FieldConfig} from '../types';

/**
 * Convert a field's `config` object from backend snake_case to frontend
 * camelCase. Shared by `records-api.ts` (config embedded in a fetched
 * Record's field definitions) and `record-types-api.ts` (config on a
 * RecordType's field definitions) — both parse the exact same wire shape,
 * so this lives in one place to avoid the two copies drifting out of sync
 * (see: object_reference TABLE columns shipping in one copy but not the
 * other).
 */
export function convertFieldConfigResponse(
  options: Record<string, unknown>
): FieldConfig {
  const result: FieldConfig = {};

  if (options.max_length !== undefined) {
    result.maxLength = options.max_length as number;
  }

  if (options.min !== undefined) {
    result.min = options.min as number;
  }

  if (options.max !== undefined) {
    result.max = options.max as number;
  }

  if (options.decimal_places !== undefined) {
    result.decimalPlaces = options.decimal_places as number;
  }

  if (options.options !== undefined) {
    result.options = options.options as string[];
  }

  if (options.role_filter !== undefined) {
    result.roleFilter = options.role_filter as string[];
  }

  if (options.multi_select !== undefined) {
    result.multiSelect = options.multi_select as boolean;
  }

  if (options.columns !== undefined) {
    result.columns = (options.columns as unknown[]).map((col: unknown) => {
      const c = col as Record<string, unknown>;
      return {
        key: c.key as string,
        label: c.label as string,
        type: c.type as
          | 'text'
          | 'textarea'
          | 'number'
          | 'date'
          | 'dropdown'
          | 'checkbox'
          | 'multi_select'
          | 'user_selector'
          | 'object_reference',
        required: c.required as boolean | undefined,
        options: c.options as string[] | undefined,
        roleFilter: (c.role_filter ?? c.roleFilter) as string[] | undefined,
        multiSelect: (c.multi_select ?? c.multiSelect) as boolean | undefined,
        referenceObjectTypeId: (c.reference_object_type_id ??
          c.referenceObjectTypeId) as string | undefined,
        multi: c.multi as boolean | undefined,
      };
    });
  }

  if (options.signature_meaning !== undefined) {
    result.signatureMeaning =
      options.signature_meaning as FieldConfig['signatureMeaning'];
  }

  if (options.max_files !== undefined) {
    result.maxFiles = options.max_files as number;
  }

  if (options.reference_object_type_id !== undefined) {
    result.referenceObjectTypeId = options.reference_object_type_id as string;
  }

  if (options.multi !== undefined) {
    result.multi = options.multi as boolean;
  }

  return result;
}
